import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface GoogleReview {
  author: string;
  photo: string | null;
  rating: number;
  text: string;
  relativeTime: string;
  time: number;
}

export interface GoogleReviewsData {
  placeId: string | null;
  name: string | null;
  rating: number | null;
  total: number;
  reviews: GoogleReview[];
  reviewUrl: string | null;
  mapsUrl: string | null;
  configured: boolean;
}

const CACHE_KEY = "haus_google_reviews_v1";
const CACHE_TTL = 60 * 60 * 1000;

export function useGoogleReviews() {
  const [data, setData] = useState<GoogleReviewsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const cached = (() => {
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.at > CACHE_TTL) return null;
        return parsed.data as GoogleReviewsData;
      } catch {
        return null;
      }
    })();

    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { data: res, error } = await supabase.functions.invoke("google-reviews");
        if (error) throw error;
        if (!active) return;
        if (res && !res.error) {
          setData(res as GoogleReviewsData);
          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: res }));
          } catch {
            /* ignore */
          }
        } else {
          setData(null);
        }
      } catch (e) {
        console.error("Failed to load Google reviews", e);
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { data, loading };
}
